import { useEffect, useMemo, useState } from 'react';
import { permissionsApi, rolesApi, usersApi } from '../../lib/api';
import { DataTable, type DataTableColumn } from '../../components/DataTable';
import { Modal } from '../../components/Modal';
import { Field, TextInput } from '../../components/Field';
import { Toggle } from '../../components/Toggle';
import { ErrorBanner } from '../../components/ErrorBanner';
import { Can } from '../../lib/permissions';
import { useShortcuts } from '../../lib/keys';
import { useCompanyStore } from '../../stores/company';
import { useUiStore } from '../../stores/ui';
import { formatIndian, parseAmountInput } from '../../lib/money';
import type { PermissionCatalogueEntry, Role, User, UserAssignment, UserDetail, UserOverride } from '../../lib/types';

type OverrideMode = 'inherit' | 'grant' | 'deny';

export function UsersPage() {
  const [users, setUsers] = useState<User[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [catalogue, setCatalogue] = useState<PermissionCatalogueEntry[]>([]);
  const [editing, setEditing] = useState<UserDetail | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const companies = useCompanyStore((s) => s.companies);
  const pushToast = useUiStore((s) => s.pushToast);

  function load() {
    usersApi.list().then(({ items }) => setUsers(items)).catch(setError);
    rolesApi.list().then(({ items }) => setRoles(items)).catch(setError);
    permissionsApi.catalogue().then(({ items }) => setCatalogue(items)).catch(setError);
  }

  useEffect(load, []);

  useShortcuts('Users', [
    { id: 'new-user', bindings: [{ key: 'n', alt: true }], description: 'New user', handler: () => { setEditing(null); setFormOpen(true); } },
  ]);

  const columns: DataTableColumn<User>[] = [
    { key: 'name', header: 'Name', render: (u) => u.fullName ?? '—' },
    { key: 'email', header: 'Email', render: (u) => <span className="mono text-[11.5px]">{u.email}</span> },
    { key: 'status', header: 'Status', render: (u) => (u.isActive ? 'Active' : <span className="text-[var(--text-faint)]">Inactive</span>) },
    { key: 'pwd', header: '', render: (u) => (u.mustChangePassword ? <span className="text-[10.5px] text-[var(--warn)]">must change pwd</span> : null) },
  ];

  return (
    <div className="flex h-full flex-col p-4">
      <div className="mb-3 flex items-center justify-between border-b border-[var(--border)] pb-2">
        <h1 className="text-[14px] font-semibold">Users</h1>
        <Can permission="admin:user:create">
          <button onClick={() => { setEditing(null); setFormOpen(true); }} className="rounded-[3px] border border-[var(--border)] px-2 py-1 text-[12px] hover:bg-[var(--bg-hover)]">
            New user <span className="kbd ml-1">Alt+N</span>
          </button>
        </Can>
      </div>

      {error ? <ErrorBanner error={error} onDismiss={() => setError(null)} /> : null}

      <div className="min-h-0 flex-1">
        <DataTable columns={columns} rows={users} getRowKey={(u) => u.id} onActivate={(u) => {
          usersApi.get(u.id).then((detail) => { setEditing(detail); setFormOpen(true); }).catch(setError);
        }} className="h-full" />
      </div>

      <Modal open={formOpen} onClose={() => setFormOpen(false)} title={editing ? `Edit — ${editing.fullName}` : 'New user'} widthClassName="max-w-2xl">
        <UserForm
          key={editing?.id ?? 'new'}
          user={editing}
          roles={roles}
          companies={companies}
          catalogue={catalogue}
          onDone={(msg) => { setFormOpen(false); pushToast({ kind: 'success', message: msg }); load(); }}
          onError={setError}
        />
      </Modal>
    </div>
  );
}

interface UserFormProps {
  user: UserDetail | null;
  roles: Role[];
  companies: { id: string; name: string }[];
  catalogue: PermissionCatalogueEntry[];
  onDone: (message: string) => void;
  onError: (err: unknown) => void;
}

function UserForm({ user, roles, companies, catalogue, onDone, onError }: UserFormProps) {
  const [email, setEmail] = useState(user?.email ?? '');
  const [fullName, setFullName] = useState(user?.fullName ?? '');
  const [password, setPassword] = useState('');
  const [roleIds, setRoleIds] = useState<string[]>(() => [...new Set(user?.roles.map((role) => role.roleId) ?? [])]);
  const [companyIds, setCompanyIds] = useState<string[]>(user?.companyIds ?? []);
  const [overrides, setOverrides] = useState<Record<string, OverrideMode>>(() => Object.fromEntries(
    (user?.overrides ?? []).filter((override) => override.companyId === null).map((override) => [override.permissionCode, override.effect]),
  ));
  const [maxBackdateDays, setMaxBackdateDays] = useState(user?.restrictions.maxBackdateDays?.toString() ?? '');
  const [voucherAmountCeiling, setVoucherAmountCeiling] = useState(user?.restrictions.voucherAmountCeiling ?? '');
  const [maskPurchaseRate, setMaskPurchaseRate] = useState(user?.restrictions.maskPurchaseRate ?? false);
  const [maskCostAndMargin, setMaskCostAndMargin] = useState(user?.restrictions.maskCostAndMargin ?? false);
  const [busy, setBusy] = useState(false);

  const rolePermissions = useMemo(() => {
    const set = new Set<string>();
    for (const r of roles.filter((r) => roleIds.includes(r.id))) for (const p of r.permissions) set.add(p);
    return set;
  }, [roles, roleIds]);

  function toggleRole(id: string) {
    setRoleIds((prev) => (prev.includes(id) ? prev.filter((r) => r !== id) : [...prev, id]));
  }
  function toggleCompany(id: string) {
    setCompanyIds((prev) => (prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]));
  }
  function cycleOverride(key: string) {
    setOverrides((prev) => {
      const current = prev[key] ?? 'inherit';
      const next: OverrideMode = current === 'inherit' ? 'grant' : current === 'grant' ? 'deny' : 'inherit';
      return { ...prev, [key]: next };
    });
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      let saved: User;
      if (user) {
        saved = await usersApi.update(user.id, { email, fullName: fullName || null, revision: user.revision });
        const existingRoles = new Map(user.roles.map((role) => [role.roleId, role]));
        const assignments: UserAssignment[] = roleIds.map((roleId) => existingRoles.get(roleId) ?? { roleId, companyId: null });
        await usersApi.setRoles(user.id, assignments);
        await usersApi.setCompanies(user.id, companyIds);
      } else {
        saved = await usersApi.create({ email, fullName: fullName || null, password });
        await usersApi.setRoles(saved.id, roleIds.map((roleId) => ({ roleId, companyId: null })));
        await usersApi.setCompanies(saved.id, companyIds);
      }
      const overridesPayload: UserOverride[] = Object.entries(overrides)
        .filter(([, mode]) => mode !== 'inherit')
        .map(([permissionCode, effect]) => ({ permissionCode, effect: effect as 'grant' | 'deny', companyId: null }));
      const scopedOverrides = user?.overrides.filter((override) => override.companyId !== null) ?? [];
      await usersApi.setOverrides(saved.id, [...scopedOverrides, ...overridesPayload]);
      await usersApi.setRestrictions(saved.id, {
        companyId: null,
        maxBackdateDays: maxBackdateDays ? Number(maxBackdateDays) : null,
        voucherAmountCeiling: voucherAmountCeiling ? parseAmountInput(voucherAmountCeiling) : null,
        maskPurchaseRate,
        maskCostAndMargin,
      });
      onDone(`${saved.fullName ?? saved.email} saved.`);
    } catch (err) {
      onError(err);
    } finally {
      setBusy(false);
    }
  }

  const domains = useMemo(() => {
    const byDomain = new Map<string, PermissionCatalogueEntry[]>();
    for (const p of catalogue) {
      const list = byDomain.get(p.domain) ?? [];
      list.push(p);
      byDomain.set(p.domain, list);
    }
    return [...byDomain.entries()];
  }, [catalogue]);

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      <div className="flex gap-3">
        <Field label="Full name" htmlFor="uf-name" className="flex-1" required>
          <TextInput id="uf-name" autoFocus value={fullName} onChange={(e) => setFullName(e.target.value)} required />
        </Field>
        <Field label="Email" htmlFor="uf-email" className="flex-1" required>
          <TextInput id="uf-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
        </Field>
      </div>

      {!user && (
        <Field label="Temporary password" htmlFor="uf-password" required hint="The new user must change this after first sign-in.">
          <TextInput id="uf-password" type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} minLength={8} required />
        </Field>
      )}

      <div>
        <p className="mb-1 text-[11px] font-medium uppercase tracking-wide text-[var(--text-dim)]">Roles</p>
        <div className="flex flex-wrap gap-x-4 gap-y-1">
          {roles.map((r) => (
            <label key={r.id} className="flex items-center gap-1.5 text-[12.5px]">
              <input type="checkbox" checked={roleIds.includes(r.id)} onChange={() => toggleRole(r.id)} />
              {r.name}
            </label>
          ))}
        </div>
      </div>

      <div>
        <p className="mb-1 text-[11px] font-medium uppercase tracking-wide text-[var(--text-dim)]">Company scoping</p>
        <div className="flex flex-wrap gap-x-4 gap-y-1">
          {companies.map((c) => (
            <label key={c.id} className="flex items-center gap-1.5 text-[12.5px]">
              <input type="checkbox" checked={companyIds.includes(c.id)} onChange={() => toggleCompany(c.id)} />
              {c.name}
            </label>
          ))}
        </div>
      </div>

      <div>
        <p className="mb-1 text-[11px] font-medium uppercase tracking-wide text-[var(--text-dim)]">
          Permission overrides <span className="normal-case text-[var(--text-faint)]">— click to cycle inherit → grant → deny. Deny always wins.</span>
        </p>
        <div className="max-h-48 overflow-y-auto border border-[var(--border)]">
          {domains.map(([domain, perms]) => (
            <div key={domain}>
              <div className="bg-[var(--bg-inset)] px-2 py-0.5 text-[10.5px] uppercase tracking-wide text-[var(--text-dim)]">{domain}</div>
              {perms.map((p) => {
                const fromRole = rolePermissions.has(p.key);
                const mode = overrides[p.key] ?? 'inherit';
                return (
                  <button
                    type="button"
                    key={p.key}
                    onClick={() => cycleOverride(p.key)}
                    className="flex w-full items-center justify-between px-2 py-0.5 text-left text-[12px] hover:bg-[var(--bg-hover)]"
                  >
                    <span className="mono text-[11.5px]">{p.key}</span>
                    <span className="flex items-center gap-1.5">
                      {fromRole && mode === 'inherit' && <span className="text-[10.5px] text-[var(--text-faint)]">via role</span>}
                      <span
                        className={
                          mode === 'grant'
                            ? 'rounded-[2px] bg-[var(--success-bg)] px-1.5 py-0.5 text-[10.5px] font-medium text-[var(--success)]'
                            : mode === 'deny'
                              ? 'rounded-[2px] bg-[var(--danger-bg)] px-1.5 py-0.5 text-[10.5px] font-medium text-[var(--danger)]'
                              : 'px-1.5 py-0.5 text-[10.5px] text-[var(--text-faint)]'
                        }
                      >
                        {mode === 'grant' ? '+ grant' : mode === 'deny' ? '− deny' : 'inherit'}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      <div>
        <p className="mb-1 text-[11px] font-medium uppercase tracking-wide text-[var(--text-dim)]">Restrictions</p>
        <div className="flex flex-col gap-2">
          <div className="flex gap-3">
            <Field label="Max backdate (days)" htmlFor="uf-backdate" className="flex-1" hint="Blank = no restriction.">
              <TextInput id="uf-backdate" type="number" min={0} value={maxBackdateDays} onChange={(e) => setMaxBackdateDays(e.target.value)} className="num" />
            </Field>
            <Field label="Voucher amount ceiling" htmlFor="uf-ceiling" className="flex-1" hint={voucherAmountCeiling ? formatIndian(parseAmountInput(voucherAmountCeiling) ?? '0') : undefined}>
              <TextInput id="uf-ceiling" inputMode="decimal" value={voucherAmountCeiling} onChange={(e) => setVoucherAmountCeiling(e.target.value)} className="num" />
            </Field>
          </div>
          <div className="flex gap-6">
            <Toggle checked={maskPurchaseRate} onChange={setMaskPurchaseRate} label="Mask purchase rate" />
            <Toggle checked={maskCostAndMargin} onChange={setMaskCostAndMargin} label="Mask cost & margin" />
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-2 border-t border-[var(--border)] pt-3">
        <button type="submit" disabled={busy} className="rounded-[3px] border border-[var(--accent)] bg-[var(--accent)] px-3 py-1.5 text-[12.5px] font-medium text-[var(--accent-fg)] disabled:opacity-50">
          {busy ? 'Saving…' : user ? 'Save changes' : 'Create user'}
        </button>
      </div>
    </form>
  );
}
